import { FC, useCallback, useState, ChangeEvent } from 'react';
import { Box, Button, InputLabel } from '@mui/material';
import FileUploaderBtn from '../common/FileUploaderBtn';
import utils from 'src/utils/utils';
import { useAlert } from 'src/hooks';
import useApiCaller from 'src/hooks/useApiCaller';
import { loaderActions } from 'src/reducers/loader';
import useActionCreators from 'src/hooks/useActionCreators';
import { getImageMaxSize } from 'src/reducers/app';
import apiClient from 'src/utils/apiClient';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

interface LoadedImgProps {
  file: File;
  dataUrl: string | ArrayBuffer | null | undefined;
}

interface ImageUploaderProps {
  label?: React.ReactNode;
  storedFileId?: number | null;
  onChange: (fileId: number | null) => void;
}

const LoadedImg: FC<LoadedImgProps> = ({ file, dataUrl }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={theme => ({
        ...theme.typography.caption,
        width: '200px',
        color: 'common.black',
        textAlign: 'center',
        padding: '2px',
      })}
    >
      <Box
        sx={{
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          height: '100px',
          width: '200px',
          backgroundPosition: 'center',
          backgroundImage: `url(${dataUrl})`,
        }}
      />
      <span>
        {file.name} ({t('file_size_kilooctet', { fileSize: (file.size / 1000).toFixed(1) })})
      </span>
    </Box>
  );
};

const ImageUploader: FC<ImageUploaderProps> = ({ label = '', storedFileId = null, onChange }) => {
  const { t } = useTranslation();
  const { showAlert } = useAlert();

  const [dataUrl, setDataUrl] = useState<LoadedImgProps['dataUrl']>();

  const onFileChange = (event?: ChangeEvent<HTMLInputElement>, newFile?: File): boolean => {
    if (!event && !newFile) {
      setDataUrl('');
      return false;
    }
    const file = newFile || (event && event.target.files ? event.target.files[0] : undefined);
    if (!file) {
      setDataUrl('');
      return false;
    }
    const pattern = /image-*/;
    const reader = new FileReader();

    if (!file.type.match(pattern)) {
      return false;
    }

    reader.onload = async () => {
      setDataUrl(reader.result);
    };

    reader.readAsDataURL(file);
    return true;
  };

  const callApi = useApiCaller();
  const { setLoader } = useActionCreators({ setLoader: loaderActions.setLoader });
  const imageMaxSize = useSelector(getImageMaxSize);

  const onSendImage = useCallback(
    async (file: File) => {
      setLoader(true);
      const handleSendFileError = async (error, _, defaultHandler) => {
        if (error.response && error.response.status === 413) {
          showAlert({
            type: 'warning',
            title: t('smartmessaging.errorMessages.warning.title'),
            msg: t('smartmessaging.error.fileTooLarge', { maxSize: imageMaxSize }),
          });
          return Promise.reject(error);
        }
        const res = await defaultHandler();
        return res;
      };

      const newStoredFileId = await callApi(
        apiClient.uploadFile,
        [file],
        handleSendFileError
      ).finally(() => setLoader(false));

      onChange(newStoredFileId);
    },
    [callApi, imageMaxSize, onChange, setLoader, showAlert, t]
  );

  return (
    <div>
      <InputLabel shrink style={{ display: 'inline-flex', fontWeight: 500 }}>
        {label}
      </InputLabel>
      {!storedFileId && (
        <FileUploaderBtn
          selectFileLabel={t('smartmessaging.config.imagefield.selectFile')}
          sendFileLabel={t('smartmessaging.config.imagefield.send')}
          cancelLabel={t('smartmessaging.config.imagefield.cancel')}
          displayCmp={(file: File) => <LoadedImg file={file} dataUrl={dataUrl} />}
          accept="image/*"
          onFileChange={onFileChange}
          onSend={onSendImage}
        />
      )}
      {storedFileId && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              height: '100px',
              width: '200px',
              backgroundPosition: 'center',
              backgroundImage: `url(${utils.link(storedFileId)})`,
            }}
          />

          <Button
            sx={{
              m: 0.5,
              bgcolor: 'warning.light',
              color: 'warning.main',
              '&:hover': {
                bgcolor: 'warning.light',
                opacity: 0.9,
              },
            }}
            onClick={event => {
              event.preventDefault();
              onChange(null);
            }}
          >
            {t('smartmessaging.config.imagefield.delete')}
          </Button>
          <Button
            sx={{ m: 0.5 }}
            color="primary"
            variant="contained"
            onClick={() => utils.downloadStoredFile(storedFileId, callApi)}
          >
            {t('smartmessaging.config.imagefield.download')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
